import { Ban, MailWarning, UserCheck, UserMinus, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMemo } from "react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import { USERS_FILTER_ALL } from "../constants/users.constants";
import type { UsersStatusFilter } from "../hooks/useUsersRoster";
import { UserStatus } from "../types/create-user.types";
import type { UserStatusCounts } from "../types/users-api.types";
import { formatCount, statusResidual } from "../utils/statusCounts";

type Accent = "neutral" | "success" | "warning" | "danger";

interface TileDef {
  key: UsersStatusFilter;
  labelKey: "tile_total" | "tile_active" | "tile_unverified" | "tile_suspended" | "tile_inactive";
  icon: LucideIcon;
  accent: Accent;
  read: (counts: UserStatusCounts) => number;
}

/**
 * Fixed, in this order. The two states that need attention sit in the middle, and a status added to the enum
 * later must NOT silently become a sixth tile — it lands in `all` and surfaces as the Total tile's residual.
 */
const TILES: readonly TileDef[] = [
  { key: USERS_FILTER_ALL, labelKey: "tile_total", icon: Users, accent: "neutral", read: (c) => c.all },
  {
    key: UserStatus.ACTIVE,
    labelKey: "tile_active",
    icon: UserCheck,
    accent: "success",
    read: (c) => c.active,
  },
  {
    key: UserStatus.PENDING_VERIFICATION,
    labelKey: "tile_unverified",
    icon: MailWarning,
    accent: "warning",
    read: (c) => c.pending_verification,
  },
  {
    key: UserStatus.SUSPENDED,
    labelKey: "tile_suspended",
    icon: Ban,
    accent: "danger",
    read: (c) => c.suspended,
  },
  {
    key: UserStatus.INACTIVE,
    labelKey: "tile_inactive",
    icon: UserMinus,
    accent: "neutral",
    read: (c) => c.inactive,
  },
];

export interface UsersPopulationStripProps {
  counts: UserStatusCounts | null;
  value: UsersStatusFilter;
  onChange: (next: UsersStatusFilter) => void;
}

/**
 * The population summary AND the status filter — one row of tiles, not a stat strip above a chip row.
 *
 * Making the tiles the control is what keeps each number in exactly one place. Two properties of the data
 * drive the rest:
 *
 * - The figures describe the **filtered set**, because `/counts` honours `search` and `role_id`. Typing in the
 *   search box changes every tile, and that is correct — each one still reports the size of its own bucket
 *   rather than of the current page.
 * - A `null` count renders `—`, never `0`. A zero on "Suspendidos" would state that nobody is suspended, which
 *   is the opposite of "we could not ask". The tiles stay selectable regardless: the filter works without the
 *   figure.
 */
export function UsersPopulationStrip({ counts, value, onChange }: UsersPopulationStripProps) {
  const { t } = useTranslations("users");

  const residual = useMemo(() => (counts ? statusResidual(counts) : 0), [counts]);

  return (
    <div
      role="group"
      aria-label={t("tile_group_label")}
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
    >
      {TILES.map((tile) => {
        const isTotal = tile.key === USERS_FILTER_ALL;
        return (
          <ColocaKpiCard
            key={tile.key}
            label={t(tile.labelKey)}
            value={formatCount(counts ? tile.read(counts) : null)}
            icon={tile.icon}
            accent={tile.accent}
            iconPlacement="end"
            selected={value === tile.key}
            onSelect={() => onChange(tile.key)}
            meta={isTotal && residual > 0 ? t("tile_other_status", { count: residual }) : undefined}
            // The Total tile spans both columns on a phone so the 2-up grid has no ragged last row.
            className={isTotal ? "col-span-2 lg:col-span-1" : undefined}
          />
        );
      })}
    </div>
  );
}
